import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
} from "@mui/material";
import { Flex } from "../../../../components/Container/Flex";
import { Secondary } from "../../../../utils/style/texts";
import Alert from "../../../../components/Alert";
import TextField from "../../../../components/Controls/TextField";
import Checkbox from "../../../../components/Controls/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ColorPicker from "../../../../components/ColorPicker";
import IconPicker from "../../../../components/IconPicker";
import BaseConfig from "../../../DashboardPage/Configs/BaseConfig";
import { Colors } from "../../../../utils/style/colors";
import {
  selectV4Scenarios,
  selectV4ScenariosFetched,
} from "../../../../redux/selectors/scenario.selector";
import { fetchV4Scenarios } from "../../../../redux/actions/scenario.actions";
import { scenarioTriggers } from "../../../../utils/models/scenarios/scenarioTriggers";
import { setExampleTile } from "../actions";
import { selectExampleTile } from "../selectors";
import { textForScenarioPick } from "./scenarioPickText";

function mapDispatchToProps(dispatch) {
  return {
    setExampleTile: (d) => dispatch(setExampleTile(d)),
    fetchV4Scenarios: () => dispatch(fetchV4Scenarios()),
  };
}

const mapStateToProps = createStructuredSelector({
  exampleTile: selectExampleTile(),
  allScenarios: selectV4Scenarios(),
  scenariosFetched: selectV4ScenariosFetched(),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ({
    onAccept,
    allScenarios,
    scenariosFetched,
    setExampleTile,
    fetchV4Scenarios,
    editConfig,
    tile,
    ...other
  }) => {
    const { t } = useTranslation();
    const [scenarioIds, setScenarioIds] = React.useState([]);
    const [ribbonColor, setRibbonColor] = React.useState(Colors.secondary());
    const [bgColor, setBgColor] = React.useState(Colors.white);
    const [text, setText] = React.useState("");
    const [icon, setIcon] = React.useState("ButtonIcon");
    const [showIcon, setShowIcon] = React.useState(true);
    // What the last scenario pick filled the text in with.
    const filledText = React.useRef("");

    // The DevCon doesn't load scenarios itself; pull the V4 set once.
    const fetchedRef = React.useRef(false);
    React.useEffect(() => {
      if (fetchedRef.current) return;
      fetchedRef.current = true;
      fetchV4Scenarios();
    }, [fetchV4Scenarios]);

    React.useEffect(() => {
      setExampleTile({
        isExample: true,
        type: "quickButton",
        config: { scenarioIds, icon, ribbonColor, bgColor, text, showIcon },
      });
      return () => setExampleTile();
    }, [setExampleTile, scenarioIds, icon, ribbonColor, bgColor, text, showIcon]);

    React.useEffect(() => {
      if (editConfig) {
        setScenarioIds(editConfig.scenarioIds || []);
        setRibbonColor(editConfig.ribbonColor || tile.color);
        setBgColor(editConfig.bgColor || Colors.white);
        setText(editConfig.text || "");
        setIcon(editConfig.icon || "ButtonIcon");
        setShowIcon(editConfig.showIcon !== false);
      }
    }, [editConfig, tile]);

    // Only quick-button scenarios are bindable, as elsewhere. The App is a V4
    // device, so bind V4 ones — their ids are the uuids it resolves.
    const availableScenarios = allScenarios.filter(
      (s) =>
        s && s.get("enabled") && s.get("trigger") === scenarioTriggers.quickButton
    );
    const selected = availableScenarios.filter(
      (s) => scenarioIds.indexOf(s.id) >= 0
    );
    // Deleted, disabled, or a V3 id from the old composer. Kept selectable so they
    // can be removed rather than silently riding along on save.
    const unresolvedIds = scenariosFetched
      ? scenarioIds.filter((id) => !availableScenarios.some((s) => s.id === id))
      : [];

    const onChangeScenarios = (ids) => {
      setScenarioIds(ids);
      const picked = availableScenarios.filter((s) => ids.indexOf(s.id) >= 0);
      const next = textForScenarioPick(text, filledText.current, picked, t);
      if (next === null) return;
      filledText.current = next;
      setText(next);
    };

    return (
      <BaseConfig
        {...other}
        disabled={!scenarioIds.length}
        onAccept={() =>
          onAccept({ scenarioIds, icon, ribbonColor, bgColor, text, showIcon })
        }
      >
        {!scenariosFetched ? (
          <Flex row align="center">
            <CircularProgress size={16} />
            <Secondary italic style={{ marginLeft: 10 }}>
              {t("loading")}...
            </Secondary>
          </Flex>
        ) : availableScenarios.size || unresolvedIds.length ? (
            <Flex col style={{ gap: 12 }}>
              <Secondary>{t("startScenario.tile.description")}</Secondary>
              {/* Label kept shrunk, or it would cover the placeholder. */}
              <FormControl variant="standard" fullWidth>
                <InputLabel id="quickButtonScenario-label" shrink>
                  {t("scenario")}
                </InputLabel>
                <MuiSelect
                  multiple
                  displayEmpty
                  margin="dense"
                  labelId="quickButtonScenario-label"
                  value={scenarioIds}
                  renderValue={() =>
                    !scenarioIds.length ? (
                      <Secondary>{t("chooseAScenario")}</Secondary>
                    ) : scenarioIds.length === 1 && selected.size === 1 ? (
                      selected.get(0).name
                    ) : (
                      t("scenarios.count", { count: scenarioIds.length })
                    )
                  }
                  onChange={(e) => onChangeScenarios(e.target.value)}
                >
                  {unresolvedIds.map((id) => (
                    <MenuItem key={id} value={id}>
                      <Checkbox checked />
                      <ListItemText>
                        {t("unknownScenario", {
                          defaultValue: "Unknown scenario",
                        })}
                      </ListItemText>
                    </MenuItem>
                  ))}
                  {availableScenarios.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={scenarioIds.indexOf(s.id) >= 0} />
                      <ListItemText>{s.get("name")}</ListItemText>
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
              <TextField
                fullWidth
                label={t("text")}
                value={text}
                onChange={setText}
              />
              <ColorPicker
                label={t("backgroundColor")}
                value={bgColor}
                onChange={setBgColor}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showIcon}
                    onChange={() => setShowIcon(!showIcon)}
                  />
                }
                label={t("showIcon", { defaultValue: "Show icon" })}
                labelPlacement="end"
              />
              {showIcon && (
                <>
                  <ColorPicker
                    label={t("ribbonColor")}
                    value={ribbonColor}
                    onChange={setRibbonColor}
                  />
                  <IconPicker
                    label={t("icon")}
                    value={icon}
                    onChange={setIcon}
                  />
                </>
              )}
            </Flex>
        ) : (
          <Alert type="info" dismissable={false}>
            {t("createAQuickButtonScenario")}
          </Alert>
        )}
      </BaseConfig>
    );
  }
);
